import { apiClient } from './api'

/**
 * バインド詳細DTO（参照情報含む）
 */
export interface BindingDetail {
  binding: ProcedureBindingAdmin
  stateRouteCount: number
  currentPublishedForProcedure: number
}

/**
 * 申請バインドDTO（Admin用）
 */
export interface ProcedureBindingAdmin {
  bindingId: string
  procedureTypeCode: string
  procedureName: string | null
  formVersionId: string
  formName: string | null
  workflowVersionId: string
  workflowName: string | null
  approvalRouteVersionId: string
  approvalRouteName: string | null
  status: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED'
  publishedAt: string | null
  description: string | null
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
  version: number
}

/**
 * 状態→ルートバインドDTO
 */
export interface StateRouteBinding {
  bindingId: string
  workflowVersionId: string
  approvalRouteVersionId: string
  stateKey: string
  routeKey: string
  procedureTypeCode: string | null
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
  version: number
}

/**
 * Lint結果
 */
export interface LintResult {
  pass: boolean
  errors: string[]
  warnings: string[]
}

/**
 * ページング結果
 */
export interface Page<T> {
  content: T[]
  totalElements: number
  totalPages: number
  size: number
  number: number
}

/**
 * バインド作成リクエスト
 */
export interface CreateBindingRequest {
  procedureTypeCode: string
  formVersionId: string
  workflowVersionId: string
  approvalRouteVersionId: string
  description?: string
  actorUserId: string
}

/**
 * バインド更新リクエスト
 */
export interface UpdateBindingRequest {
  formVersionId: string
  workflowVersionId: string
  approvalRouteVersionId: string
  description?: string
  actorUserId: string
  version: number
}

/**
 * バインド管理サービス（Admin用）
 */
export class BindingAdminService {
  /**
   * バインド一覧取得（ページング）
   */
  static async getBindings(params?: {
    q?: string
    status?: string
    procedureTypeCode?: string
    page?: number
    size?: number
    sort?: string
  }): Promise<Page<ProcedureBindingAdmin>> {
    const queryParams = new URLSearchParams()
    if (params?.q) queryParams.append('q', params.q)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.procedureTypeCode) queryParams.append('procedure_type_code', params.procedureTypeCode)
    if (params?.page !== undefined) queryParams.append('page', params.page.toString())
    if (params?.size !== undefined) queryParams.append('size', params.size.toString())
    if (params?.sort) queryParams.append('sort', params.sort)

    const url = `/api/admin/bindings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return apiClient.get<Page<ProcedureBindingAdmin>>(url)
  }

  /**
   * バインド詳細取得
   */
  static async getBinding(bindingId: string): Promise<BindingDetail> {
    return apiClient.get<BindingDetail>(`/api/admin/bindings/${bindingId}`)
  }

  /**
   * バインド新規作成
   */
  static async createBinding(request: CreateBindingRequest): Promise<{ bindingId: string }> {
    return apiClient.post<{ bindingId: string }>('/api/admin/bindings', request)
  }

  /**
   * バインド更新
   */
  static async updateBinding(bindingId: string, request: UpdateBindingRequest): Promise<{ status: string }> {
    return apiClient.put<{ status: string }>(`/api/admin/bindings/${bindingId}`, request)
  }

  /**
   * バインド複製
   */
  static async duplicateBinding(bindingId: string, actorUserId: string): Promise<{ bindingId: string }> {
    return apiClient.post<{ bindingId: string }>(`/api/admin/bindings/${bindingId}/duplicate`, { actorUserId })
  }

  /**
   * バインド有効化
   */
  static async activateBinding(bindingId: string, actorUserId: string): Promise<{ status: string }> {
    return apiClient.post<{ status: string }>(`/api/admin/bindings/${bindingId}/activate`, { actorUserId })
  }

  /**
   * バインド無効化
   */
  static async deactivateBinding(bindingId: string, actorUserId: string): Promise<{ status: string }> {
    return apiClient.post<{ status: string }>(`/api/admin/bindings/${bindingId}/deactivate`, { actorUserId })
  }

  /**
   * バインド廃止
   */
  static async archiveBinding(bindingId: string, actorUserId: string): Promise<{ status: string }> {
    return apiClient.post<{ status: string }>(`/api/admin/bindings/${bindingId}/archive`, { actorUserId })
  }

  /**
   * バインドLint実行
   */
  static async lintBinding(bindingId: string): Promise<LintResult> {
    return apiClient.post<LintResult>(`/api/admin/bindings/${bindingId}/lint`)
  }

  /**
   * 状態→ルート割当一覧取得
   */
  static async getStateRoutes(bindingId: string): Promise<StateRouteBinding[]> {
    return apiClient.get<StateRouteBinding[]>(`/api/admin/bindings/${bindingId}/state-routes`)
  }

  /**
   * 状態→ルート割当更新
   */
  static async updateStateRoutes(
    bindingId: string,
    stateRoutes: StateRouteBinding[],
    actorUserId: string
  ): Promise<{ status: string }> {
    return apiClient.put<{ status: string }>(`/api/admin/bindings/${bindingId}/state-routes`, {
      stateRoutes,
      actorUserId
    })
  }
}
